/**
 * @file BreadcrumbsTab.tsx
 * @description Вкладка «Крошки» панели «Весь сайт → Меню» (phase19.16,
 * партия «SEO-техника», v0.19.65; SB146, SB33; макет uiux-seo-tech.md §3.3;
 * решение пользователя 2026-10-25: панель остаётся «Меню», крошки —
 * вкладкой `sub=crumbs`).
 *
 * «Показывать хлебные крошки» — документ навигации части MENUS
 * (`useNavigationDraft`): правка сохраняется в черновик, попадает в ●N
 * панели и «Отменить изменения», на сайт — после «Опубликовать». Ниже —
 * образец «Так на сайте», подсказки (товар — элемент шаблона «Хлебные
 * крошки» со ссылкой «Открыть шаблон товара →»; главная и оформление заказа
 * — никогда), страницы со своей настройкой (переход в их «Настройки страницы
 * → Для поиска»). Если на канве главная — плашка «откройте другую страницу».
 */

import { ChevronRight, Info } from "lucide-react";
import { useContext, type ReactNode } from "react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { BuilderContext } from "../../site-builder/builder-context";
import { SwitchRow } from "../counters/SwitchRow";
import { SaveIndicator } from "../SaveIndicator";
import { useSiteView } from "../site-view-context";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import {
  BREADCRUMBS_INTRO,
  BREADCRUMBS_NEVER_NOTE,
  BREADCRUMBS_PAGE_NOTE,
  BREADCRUMBS_PRODUCT_NOTE,
  BREADCRUMBS_SAMPLE_OFF,
  BREADCRUMBS_SAMPLE_ON,
  BREADCRUMBS_SWITCH_HINT,
  PAGE_BREADCRUMBS_OWN,
  pagesWithOwnBreadcrumbs,
} from "../../../lib/site-builder/page-breadcrumbs";

/** Строка подсказки с ⓘ. */
function Note({ children }: { children: ReactNode }) {
  return (
    <div className="type-caption flex items-start gap-2 text-on-surface-variant">
      <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-col items-start gap-1">{children}</div>
    </div>
  );
}

export function BreadcrumbsTab() {
  const view = useSiteView();
  const nav = useSiteDraftPart("navigation");
  const b = useContext(BuilderContext);
  const canEdit = view.editableParts.includes("MENUS");
  const on = nav.navigation.breadcrumbs;
  const own = pagesWithOwnBreadcrumbs(b?.pages.list ?? null);
  // На канве главная — крошек на ней нет никогда; предложить страницу, где их видно.
  const onHome = b !== null && b.selection.screen === null && b.pages.page?.kind === "SYSTEM_HOME";
  const sample = onHome ? (b?.pages.list ?? []).find((p) => p.kind === "CUSTOM" && p.isPublished && !p.deletedAt) : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-body text-on-surface-variant">{BREADCRUMBS_INTRO}</p>
      <SaveIndicator state={nav} />
      {onHome ? (
        // Кнопка под текстом, а не справа: на 360 текст рядом с кнопкой сжимался в столбик.
        <NoticeStrip>
          <span className="flex flex-col items-start gap-2">
            На главной крошек нет — откройте другую страницу, чтобы увидеть их.
            {sample && b ? (
              <Button variant="outline" className="max-sm:w-full" onClick={() => void b.select({ pageId: sample.id, screen: null, sel: { kind: "page" } })}>
                Открыть «{sample.title}»
              </Button>
            ) : null}
          </span>
        </NoticeStrip>
      ) : null}
      <SwitchRow label="Показывать хлебные крошки" hint={BREADCRUMBS_SWITCH_HINT} checked={on} disabled={!canEdit} onChange={nav.setBreadcrumbs} />
      <div className="flex min-w-0 flex-col gap-1 rounded-xl bg-surface-low p-4" aria-label="Так на сайте">
        <span className="type-caption text-on-surface-variant">Так на сайте</span>
        <span className={on ? "type-body text-on-surface" : "type-caption text-on-surface-variant"}>{on ? BREADCRUMBS_SAMPLE_ON : BREADCRUMBS_SAMPLE_OFF}</span>
      </div>
      <Note>
        <span>{BREADCRUMBS_PRODUCT_NOTE}</span>
        {b ? (
          <Button variant="link" className="min-h-12 px-0 lg:min-h-11" onClick={() => void b.select({ pageId: null, screen: "product", panel: null, sel: { kind: "page" } })}>
            Открыть шаблон товара →
          </Button>
        ) : null}
      </Note>
      <Note>
        <span>{BREADCRUMBS_NEVER_NOTE}</span>
      </Note>
      {own.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium text-on-surface">Своя настройка у страниц ({own.length})</span>
          <ul className="flex flex-col">
            {own.map((page) => (
              <li key={page.id}>
                <Button
                  variant="ghost"
                  className="min-h-12 w-full justify-between px-2"
                  onClick={() => void b?.select({ pageId: page.id, screen: null, panel: null, sel: { kind: "page" }, group: "seo" })}
                >
                  <span className="truncate">
                    {page.title} — {page.breadcrumbs === "SHOW" ? PAGE_BREADCRUMBS_OWN.SHOW : PAGE_BREADCRUMBS_OWN.HIDE}
                  </span>
                  <ChevronRight aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="type-caption text-on-surface-variant">{BREADCRUMBS_PAGE_NOTE}</p>
    </div>
  );
}
